import { useState, useEffect } from 'react';
import api from '../services/api';
import { API } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { FileText, Download, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const FileManager = () => {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [csvContent, setCsvContent] = useState('');
  const [loadingCsv, setLoadingCsv] = useState(false);

  useEffect(() => {
    loadUploads();
  }, []);

  const loadUploads = async () => {
    setLoading(true);
    try {
      const response = await api.get(`${API}/upload/latest-uploads`);
      // API returns array directly, not nested in uploads property
      setUploads(response.data || []);
    } catch (error) {
      console.error('Error loading uploads:', error);
      toast.error('Failed to load uploads');
      setUploads([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd MMM yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  const viewCsvContent = async (upload) => {
    setSelectedFile(upload);
    setLoadingCsv(true);
    try {
      const response = await api.get(`${API}/upload/csv-content/${upload.id}`);
      setCsvContent(response.data.content);
    } catch (error) {
      console.error('Error loading CSV content:', error);
      toast.error('Failed to load CSV content');
    } finally {
      setLoadingCsv(false);
    }
  };

  // Filter uploads by type
  const orderBookUploads = uploads.filter(u => u.file_type === 'order_book');
  const otherUploads = uploads.filter(u => u.file_type !== 'order_book');

  const renderFileList = (fileList) => {
    if (fileList.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          No files uploaded yet. Upload a CSV file from the Dashboard edit screen.
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {fileList.map((upload) => (
          <div
            key={upload.id}
            className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1">
                <div className="mt-1">
                  <FileText className="w-5 h-5 text-blue-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 truncate">
                    {upload.filename}
                  </div>
                  <div className="text-sm text-gray-500 mt-1 space-y-1">
                    <div>Uploaded by: <span className="font-medium">{upload.uploaded_by}</span></div>
                    <div>Date: <span className="font-medium">{formatDate(upload.uploaded_at)}</span></div>
                    {upload.record_count && (
                      <div>Records: <span className="font-medium text-green-600">{upload.record_count}</span></div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 ml-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => viewCsvContent(upload)}
                  className="gap-2"
                >
                  <FileText className="w-4 h-4" />
                  View
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>File Manager</CardTitle>
            <CardDescription>View and manage uploaded files</CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadUploads}
            disabled={loading}
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="order-book" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="order-book">Order Book</TabsTrigger>
            <TabsTrigger value="others">Others</TabsTrigger>
          </TabsList>
          
          <TabsContent value="order-book" className="mt-4">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : (
              renderFileList(orderBookUploads)
            )}
          </TabsContent>
          
          <TabsContent value="others" className="mt-4">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : (
              renderFileList(otherUploads)
            )}
          </TabsContent>
        </Tabs>

        {/* File Details Modal */}
        {selectedFile && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-6xl w-full max-h-[90vh] overflow-auto">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>CSV File Viewer</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedFile(null);
                      setCsvContent('');
                    }}
                  >
                    Close
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 pb-4 border-b">
                    <div>
                      <div className="text-sm text-gray-500">Filename</div>
                      <div className="font-semibold">{selectedFile.filename}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Uploaded By</div>
                      <div className="font-semibold">{selectedFile.uploaded_by}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Uploaded At</div>
                      <div className="font-semibold">{formatDate(selectedFile.uploaded_at)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Total Records</div>
                      <div className="font-semibold text-green-600">{selectedFile.record_count}</div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="text-sm font-semibold text-gray-700 mb-2">CSV Content:</div>
                    {loadingCsv ? (
                      <div className="text-center py-8 text-gray-500">Loading CSV...</div>
                    ) : (
                      <div className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                        <pre className="text-xs font-mono whitespace-pre">{csvContent}</pre>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FileManager;
